import { UnorderedListOutlined } from "@ant-design/icons";
import { Button, Tag } from "antd";
import { DefaultOptionType } from "antd/es/select";
import { ColumnsType } from "antd/es/table";
import { t } from "i18next";
import { PaginationPayload } from "../../types";
import { FilterItem, FilterItemType } from "../filters/filters.types";
import {
  Monitoring,
  MonitoringDetails,
  MonitoringListFilterPayload,
  Tracking,
  TrackingListFilterPayload,
} from "./trackAndTrace.types";

export const MONITORING_LIST_INITIAL_FILTERS: MonitoringListFilterPayload = {
  drustvoSlanjePIB: null,
  brojOtpremnice: null,
  drustvoPIB: null,
  pageNumber: 1,
  pageSize: 10,
};

export const TRACKING_LIST_INITIAL_FILTERS: TrackingListFilterPayload = {
  kod: null,
  pib: null,
  status: null,
  tip: null,
  pageNumber: 1,
  pageSize: 10,
};

export const MONITORING_LIST_INITIAL_PAGINATION: PaginationPayload = {
  pageNumber: 1,
  pageSize: 10,
};

export const TRACKING_LIST_INITIAL_PAGINATION: PaginationPayload = {
  pageNumber: 1,
  pageSize: 10,
};

export const getMonitoringTableColumns = ({
  onDetailClickHandler,
}: {
  onDetailClickHandler: (id: number) => void;
}) => {
  return [
    {
      title: t("TRACK.ID_OTPREMNICE"),
      dataIndex: "brojOtpremnice",
    },
    {
      title: t("TRACK.ID_SLANJA"),
      dataIndex: "slanjeId",
    },
    {
      title: t("TRACK.DOGADJAJ_NEUPARIVANJA"),
      width: "180px",
      render: (record: Monitoring) => {
        return <Tag>{record.monitoringActionNaziv}</Tag>;
      },
    },
    { title: t("TRACK.NAZIV_DRUSTVA"), dataIndex: "drustvoNaziv" },
    {
      title: t("TRACK.PIB_DRUSTVA"),
      dataIndex: "drustvoPIB",
    },
    {
      title: t("TRACK.ADRESA_OTPREME"),
      dataIndex: "adresaOtpreme",
    },
    {
      title: t("TRACK.NAZIV_DRUSTVA_NA_KOJE_SE_SALJE"),
      dataIndex: "drustvoSlanjeNaziv",
    },
    {
      title: t("TRACK.PIB_DRUSTVA_NA_KOJE_SE_SALJE"),
      dataIndex: "drustvoSlanjePIB",
    },
    {
      title: t("TRACK.ADRESA_ODREDISTA"),
      dataIndex: "adresaOdredista",
      width: "120px",
    },

    {
      title: t("TRACK.DETALJI"),
      fixed: "right",
      align: "start",
      width: "150px",
      render: (record: Monitoring) => {
        return (
          <Button
            type="primary"
            icon={
              <UnorderedListOutlined
                onClick={() => {
                  onDetailClickHandler(record?.id);
                }}
              />
            }
            block
          >
            {t("TRACK.DETALJI")}
          </Button>
        );
      },
    },
  ] as ColumnsType<Monitoring>;
};

export const getMonitoringDetailsTableColumns = () => {
  return [
    {
      title: t("TRACK.TIP_PAKOVANJA"),
      dataIndex: "tipPakovanjaNaziv",
    },
    {
      title: t("TABLES.NAZIV_ROBNE_MARKE"),
      dataIndex: "robnaMarkaNaziv",
    },
    {
      title: t("COMMON.KOLICINA"),
      dataIndex: "kolicina",
    },
  ] as ColumnsType<MonitoringDetails>;
};

export const getMonitoringListFilterItems = (): Array<
  FilterItem<MonitoringListFilterPayload>
> => {
  return [
    {
      fieldName: "drustvoPIB",
      type: FilterItemType.TEXT,
      props: {
        placeholder: t("TRACK.PIB_DRUSTVA"),
        allowClear: true,
      },
    },
    {
      fieldName: "drustvoSlanjePIB",
      type: FilterItemType.TEXT,
      props: {
        placeholder: t("TRACK.PIB_DRUSTVA_NA_KOJE_SE_SALJE"),
        allowClear: true,
      },
    },
    {
      fieldName: "brojOtpremnice",
      type: FilterItemType.TEXT,
      props: {
        placeholder: t("TRACK.BROJ_OTPREMNICE"),
        allowClear: true,
      },
    },
  ];
};

export const getTrackingTableColumns = ({
  renderCodeCell,
  hiddenColumns,
  postTtIstorijaKodaSastav,
  clearSastav,
  className,
}: {
  renderCodeCell: (
    filters: Partial<TrackingListFilterPayload>,
    value: string | undefined
  ) => void;
  hiddenColumns: Array<keyof Tracking>;
  clearSastav: (id: number) => void;
  postTtIstorijaKodaSastav: (
    id: number,
    tipPakovanjaId: number
  ) => Promise<void>;
  className?: string;
}) => {
  const columns = [
    {
      title: t("TRACK.TIP_PAKOVANJA"),
      dataIndex: "tipPakovanja",
      key: "tipPakovanja",
      fixed: true,
      className,
    },
    {
      title: t("TRACK.IDENTIFIKACIONI_KOD"),
      key: "idKod",
      fixed: true,
      className,
      render: (row: Tracking) =>
        renderCodeCell({ kod: row.idKod, tip: 1 }, row.idKod),
    },
    {
      title: t("TRACK.BOKS"),
      key: "boks",
      className,
      render: (row: Tracking) =>
        renderCodeCell({ kod: row.boks, tip: 2 }, row.boks),
    },
    {
      title: "Master case",
      key: "masterCase",
      className,
      render: (row: Tracking) =>
        renderCodeCell({ kod: row.masterCase, tip: 3 }, row.masterCase),
    },
    {
      title: t("TRACK.PALETA"),
      key: "paleta",
      className,
      render: (row: Tracking) =>
        renderCodeCell({ kod: row.paleta, tip: 4 }, row.paleta),
    },
    {
      title: t("TRACK.ZBIRNO_PAKOVANJE"),
      key: "zbirnoPakovanje",
      className,
      render: (row: Tracking) =>
        renderCodeCell(
          { kod: row.zbirnoPakovanje, tip: 5 },
          row.zbirnoPakovanje?.toString()
        ),
    },
    {
      title: t("PU_OBRASCI.PIB"),
      dataIndex: "pib",
      className,
      key: "pib",
    },
    {
      title: t("TRACK.NAZIV_DRUSTVA"),
      dataIndex: "nazivDrustva",
      key: "nazivDrustva",
      className,
    },
    {
      title: t("TRACK.OBJEKAT"),
      dataIndex: "objekat",
      key: "objekat",
      className,
    },
    {
      title: t("TRACK.DRUSTVO_SLANJE"),
      dataIndex: "drustvoSlanje",
      key: "drustvoSlanje",
      className,
    },
    {
      title: t("TRACK.OBJEKAT_SALJE_T"),
      dataIndex: "objekatSlanje",
      key: "objekatSlanje",
      className,
    },
    {
      title: t("PLACEHOLDERS.STATUS"),
      dataIndex: "status",
      key: "status",
      className,
    },
    {
      title: t("TRACK.DATUM_VREME"),
      dataIndex: "datum",
      key: "datum",
      className,
      responsive: false,
    },

    {
      title: t("TRACK.SASTAV"),
      className,
      render: (record: Tracking) =>
        !record.nested &&
        record.tipPakovanjaId !== 1 && (
          <Button
            onClick={(event) => {
              event.stopPropagation();
              record.children
                ? clearSastav(record.id!)
                : postTtIstorijaKodaSastav(
                    record.id as number,
                    record.tipPakovanjaId as number
                  );
            }}
            block
          >
            {record.children ? t("COMMON.ZATVORI") : t("TRACK.SASTAV")}
          </Button>
        ),
    },
  ] as ColumnsType<Tracking>;

  return columns.filter(
    (column) => !hiddenColumns.includes(column.key as keyof Tracking)
  );
};

export const getTrackingListFilterItems = ({
  isLoading,
  statuses,
  packageTypes,
  hiddenFilters,
}: {
  isLoading: boolean;
  statuses: DefaultOptionType[];
  packageTypes: DefaultOptionType[];
  hiddenFilters?: Array<keyof TrackingListFilterPayload>;
}): Array<FilterItem<TrackingListFilterPayload>> => {
  const filters = [
    {
      fieldName: "tip",
      type: FilterItemType.SELECT,
      props: {
        placeholder: t("TRACK.TIP"),
        options: packageTypes,
        loading: isLoading,
      },
    },
    {
      fieldName: "pib",
      type: FilterItemType.TEXT,
      props: {
        placeholder: t("PU_OBRASCI.PIB"),
        allowClear: true,
      },
    },
    {
      fieldName: "kod",
      type: FilterItemType.TEXT,
      props: {
        placeholder: t("TRACK.KOD"),
        allowClear: true,
      },
    },

    {
      fieldName: "status",
      type: FilterItemType.SELECT,
      props: {
        placeholder: t("PLACEHOLDERS.STATUS"),
        options: statuses,
        loading: isLoading,
      },
    },
  ];

  return filters.filter(
    (filter) =>
      !hiddenFilters?.includes(
        filter.fieldName as keyof TrackingListFilterPayload
      )
  ) as Array<FilterItem<TrackingListFilterPayload>>;
};
